import apiClient from './api';

/**
 * Member Lab Service
 * ดึงข้อมูล Lab Results ของ user ที่ login อยู่
 * ใช้ API: /GetLabData.php (server.dietz.asia) — ดึงเฉพาะข้อมูลของ user คนนั้นตรงๆ
 */

/**
 * ดึงข้อมูล Lab ทั้งหมดของ user ผ่าน /GetLabData.php
 * วน page จนครบ แล้ว format ให้ตรงกับ structure เดิม { labData, profile, visitDates }
 */
export const getMemberLabData = async () => {
  try {
    // ดึง campaign data (ข้อมูลจากโปรแกรมตรวจ MFU)
    const firstRes = await apiClient.post('/GetLabData.php', {});

    if (!firstRes?.data || !Array.isArray(firstRes.data)) {
      return null;
    }

    const campaignData = firstRes.data;

    // หา visitDates จาก campaign data
    const visitDatesSet = new Set();
    campaignData.forEach(item => {
      if (item.dateadd) {
        visitDatesSet.add(item.dateadd.split(' ')[0]);
      }
    });

    // ดึงข้อมูลที่ user กรอกเอง โดย fetch แต่ละวันที่พบ + 30 วันล่าสุด
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      visitDatesSet.add(dateStr);
    }

    // Fetch ข้อมูล user-entered สำหรับแต่ละวัน แบบ parallel
    const dateList = [...visitDatesSet];
    const dateResponses = await Promise.allSettled(
      dateList.map(date => apiClient.post('/GetLabData.php', { dateadd: date }))
    );

    // Merge ข้อมูลทั้งหมด (campaign + user-entered) โดยไม่ซ้ำกัน
    const seen = new Set();
    const allData = [...campaignData];

    campaignData.forEach(item => {
      seen.add(`${item.dateadd}_${item.name}_${item.value}`);
    });

    dateResponses.forEach(res => {
      if (res.status === 'fulfilled' && Array.isArray(res.value?.data)) {
        res.value.data.forEach(item => {
          const key = `${item.dateadd}_${item.name}_${item.value}`;
          if (!seen.has(key)) {
            seen.add(key);
            allData.push(item);
          }
        });
      }
    });

    return formatLabData(allData);
  } catch (error) {
    return null;
  }
};

/**
 * แปลงข้อมูลจาก /GetLabData.php ให้อยู่ใน format { labData, profile, visitDates }
 * แต่ละ item: { dateadd, name, value, memberid, campaignid, checkplace, note, uploader }
 */
const formatLabData = (items) => {
  if (!Array.isArray(items) || items.length === 0) return null;

  const allLabData = {};
  const visitDatesSet = new Set();

  const addLabEntry = (key, value, date) => {
    if (!key || value == null || value === '') return;

    if (!allLabData[key]) allLabData[key] = [];

    // ป้องกันรายการซ้ำในวันเดียวกัน
    const isDuplicate = allLabData[key].some(e => e.date === date && e.value === value);
    if (!isDuplicate) {
      allLabData[key].push({ value, date, unit: '' });
    }
  };

  items.forEach(item => {
    const rawName = item.name;
    const rawValue = String(item.value ?? '').trim();
    const examDate = item.dateadd;

    if (!rawName || rawValue === '') return;

    const name = normalizeLabName(rawName);
    const date = examDate;

    if (date) visitDatesSet.add(date.split(' ')[0]);

    let cleanValue = rawValue;
    const compactName = rawName.toLowerCase().replace(/[^a-z0-9]/g, '');

    // eGFR format "111.8[Stage1]" → เอาเฉพาะตัวเลข
    if (name === 'egfr' && cleanValue.includes('[')) {
      cleanValue = cleanValue.split('[')[0].trim();
    }

    // ลบ comma ออกจากตัวเลข เช่น "6,110" → "6110"
    if (cleanValue.includes(',')) {
      cleanValue = cleanValue.replace(/,/g, '');
    }

    // รองรับความดันแบบ combined field เช่น sys/dias หรือ sysdias
    // เพื่อให้หน้าเปรียบเทียบดึงได้เหมือน admin ที่แยกตัวบน/ตัวล่าง
    if (compactName === 'sysdias') {
      const [systolicValue, diastolicValue] = cleanValue.split('/').map(v => v.trim());
      addLabEntry('systolic', systolicValue, date);
      addLabEntry('diastolic', diastolicValue, date);
      return;
    }

    addLabEntry(name, cleanValue, date);
  });

  // เรียงล่าสุดก่อน
  Object.keys(allLabData).forEach(key => {
    allLabData[key].sort((a, b) => new Date(b.date) - new Date(a.date));
  });

  const visitDates = [...visitDatesSet].sort((a, b) => new Date(b) - new Date(a));

  return {
    profile: {},
    labData: allLabData,
    visitDates,
  };
};

/**
 * Normalize ชื่อ lab parameter ให้ตรงกับที่ใช้ใน app
 */
const normalizeLabName = (name) => {
  const nameMap = {
    // Kidney
    'kidneyfunctiontestcreatinine': 'creatinine',
    'kidneyfunctiontestbun': 'bun',
    'kidneyfunctiontestegfr': 'egfr',
    'creatininecreatinine': 'creatinine',
    'creatinineegfr': 'egfr',
    'creatinineegfrresult': 'egfr',
    'creatinine': 'creatinine',
    'bun': 'bun',
    'egfr': 'egfr',

    // CBC - ใช้ชื่อเต็มจาก API
    'cbcrbc': 'rbc',
    'cbchemoglobin': 'hemoglobin',
    'cbchematocrit': 'hematocrit',
    'cbcwbc': 'wbc',
    'cbcplatelet': 'platelet',
    'cbcplt': 'platelet',
    'cbcpltcount': 'platelet',
    'cbcmcv': 'mcv',
    'cbcmch': 'mch',
    'cbcmchc': 'mchc',
    'cbcrdw': 'rdw',
    'cbcrdwresult': 'rdw',
    'cbcmpv': 'mpv',
    'cbcmpvresult': 'mpv',
    'cbceosinophil': 'eosinophil',
    'cbceosinophilcount': 'eosinophil',
    'cbceosinophilresult': 'eosinophil',
    'wbc': 'wbc',
    'hb': 'hemoglobin',
    'hct': 'hematocrit',
    'plt': 'platelet',
    'rbc': 'rbc',
    'hemoglobin': 'hemoglobin',
    'hematocrit': 'hematocrit',
    'platelet': 'platelet',
    'mcv': 'mcv',
    'mch': 'mch',
    'mchc': 'mchc',
    'rdw': 'rdw',
    'rdwresult': 'rdw',
    'mpv': 'mpv',
    'mpvresult': 'mpv',
    'eosinophil': 'eosinophil',
    'eosinophilcount': 'eosinophil',
    'neutrophile': 'neutrophile',
    'lymphocyte-count': 'lymphocyte-count',
    'monocyte-count': 'monocyte-count',
    'basophil-count': 'basophil-count',
    'lymphocytecount': 'lymphocyte-count',
    'monocytecount': 'monocyte-count',
    'basophilcount': 'basophil-count',

    // HbA1c
    'diabeteshba1c': 'hba1c',
    'hba1c': 'hba1c',

    // Lipid - App ใช้ชื่อ: 'chol', 'hdl', 'ldl', 'tg'
    'lipidprofilecholesterol': 'chol',
    'lipidprofilehdl': 'hdl',
    'lipidprofileldl': 'ldl',
    'lipidprofiletriglyceride': 'tg',
    'cholesterol': 'chol',
    'chol': 'chol',
    'hdl': 'hdl',
    'ldl': 'ldl',
    'triglyceride': 'tg',
    'tg': 'tg',
    'triglycerideresult': 'tg',

    // Liver
    'liverfunctiontestsgot': 'sgot',
    'liverfunctiontestsgpt': 'sgpt',
    'liverfunctiontestsgotast': 'sgot',
    'liverfunctiontestsgotastresult': 'sgot',
    'liverfunctiontestalk': 'alp',
    'liverfunctiontestalkp': 'alp',
    'liverfunctiontestalkalinephosphatase': 'alp',
    'liverfunctiontestalkalinephosphataseresult': 'alp',
    'sgot': 'sgot',
    'sgpt': 'sgpt',
    'ast': 'sgot',
    'alt': 'sgpt',
    'alp': 'alp',
    'alkp': 'alp',
    'alkalinephosphatase': 'alp',
    // Liver - Total Bilirubin
    'liverfunctiontestbilirubintotal': 'total_bilirubin',
    'liverfunctiontestbilirubintotalresult': 'total_bilirubin',
    'bilirubintotal': 'total_bilirubin',
    'totalbilirubin': 'total_bilirubin',
    'bilirubin': 'total_bilirubin',
    // Liver - Direct Bilirubin
    'liverfunctiontestbilirubindirect': 'direct_bilirubin',
    'liverfunctiontestbilirubindirectresult': 'direct_bilirubin',
    'bilirubindirect': 'direct_bilirubin',
    'directbilirubin': 'direct_bilirubin',
    // Liver - Albumin
    'liverfunctiontestalbumin': 'albumin',
    'liverfunctiontestalbuminresult': 'albumin',
    'albumin': 'albumin',
    // Liver - Total Protein
    'liverfunctiontesttotalprotein': 'total_protein',
    'liverfunctiontesttotalproteinresult': 'total_protein',
    'totalprotein': 'total_protein',
    'totalproteinresult': 'total_protein',
    // Liver - GGT
    'liverfunctiontestggt': 'ggt',
    'liverfunctiontestggtresult': 'ggt',
    'ggt': 'ggt',
    // Liver - Globulin
    'liverfunctiontestglobulin': 'globulin',
    'liverfunctiontestglobulinresult': 'globulin',
    'globulin': 'globulin',

    // Uric Acid
    'uricacid': 'uric',
    'uric_acid': 'uric',
    'uric': 'uric',
    'uricacidresult': 'uric',

    // Blood Pressure & Sugar
    'systolicbloodpressure': 'systolic',
    'diastolicbloodpressure': 'diastolic',
    'bloodpressuresystolic': 'systolic',
    'bloodpressurediastolic': 'diastolic',
    'sys': 'systolic',
    'dia': 'diastolic',
    'fastingbloodsugar': 'fbs/fpg',
    'diabetesfbs': 'fbs/fpg',
    'fbs': 'fbs/fpg',
    'fbsfpg': 'fbs/fpg',
    'fbs/fpg': 'fbs/fpg',
    'fpg': 'fbs/fpg',
    'sugar': 'fbs/fpg',

    // Physical
    'weight': 'weight',
    'height': 'height',
    'bmi': 'bmi',
    'waistline': 'waistline',
    'pulse': 'pulse',
    'respiratoryrate': 'respiratory_rate',

    // Urine Test - ใช้ชื่อจาก API จริง (มีหลายรูปแบบ)
    'urinalysisphresult': 'urinalysis_ph',
    'urinalysiscolorresul': 'urinalysis_color',
    'urinalysiscolorresult': 'urinalysis_color',
    'urinalysisapperancer': 'urinalysis_appearance',
    'urinalysisapperanceresult': 'urinalysis_appearance',
    'urinalysisspecificgr': 'urinalysis_specific_gravity',
    'urinalysisspecificgrresult': 'urinalysis_specific_gravity',
    'urinalysisproteinres': 'urinalysis_protein',
    'urinalysisproteinresult': 'urinalysis_protein',
    'urinalysissugarresul': 'urinalysis_glucose',
    'urinalysissugarresult': 'urinalysis_glucose',
    'urinalysisketoneresu': 'urinalysis_ketone',
    'urinalysisketoneresult': 'urinalysis_ketone',
    'urinalysisbloodresul': 'urinalysis_blood',
    'urinalysisbloodresult': 'urinalysis_blood',
    'urinalysisbilirubinresult': 'urinalysis_bilirubin',
    'urinalysisurobilinogenresult': 'urinalysis_urobilinogen',
    'urinalysisleukocyteresult': 'urinalysis_leukocyte',
    'urinalysisnitriteresult': 'urinalysis_nitrite',
    'urinalysisrbcresult': 'urinalysis_rbc',
    'urinalysiswbcresult': 'urinalysis_wbc',
    'urinalysisph': 'urinalysis_ph',
    'urinalysiscolor': 'urinalysis_color',
    'urinalysisappearance': 'urinalysis_appearance',
    'urinalysisspecificgravity': 'urinalysis_specific_gravity',
    'urinalysisprotein': 'urinalysis_protein',
    'urinalysisglucose': 'urinalysis_glucose',
    'urinalysisketone': 'urinalysis_ketone',
    'urinalysisblood': 'urinalysis_blood',
    'urinalysisbilirubin': 'urinalysis_bilirubin',
    'urinalysisurobilinogen': 'urinalysis_urobilinogen',
    'urinalysisleukocyte': 'urinalysis_leukocyte',
    'urinalysisnitrite': 'urinalysis_nitrite',
    'urinalysisrbc': 'urinalysis_rbc',
    'urinalysiswbc': 'urinalysis_wbc',
    'urinalysisbacteriaresult': 'bacteria',
    'urinalysisbacteria': 'bacteria',
    'urinalysisbactcellre': 'bacteria', // API truncated name
    'bacteria': 'bacteria',
    'bacteriaresult': 'bacteria',
    'urinalysisepithelialresult': 'epithelial',
    'urinalysisepithelial': 'epithelial',
    'epithelial': 'epithelial',
    'epithelialresult': 'epithelial',
    'epithelialcells': 'epithelial',
    'epithelialcellsresult': 'epithelial',

    // Urine Cast Types
    'urinalysishyalinecastresult': 'hyaline_cast',
    'urinalysishyalinecast': 'hyaline_cast',
    'urinalysishyalinecas': 'hyaline_cast', // API truncated name (ตัดท้าย)
    'hyalinecast': 'hyaline_cast',
    'hyalinecastresult': 'hyaline_cast',
    'urinalysisgranularcastresult': 'granular_cast',
    'urinalysisgranularcast': 'granular_cast',
    'urinalysisgranularca': 'granular_cast', // API truncated name (ตัดท้าย)
    'granularcast': 'granular_cast',
    'granularcastresult': 'granular_cast',
    'urinalysiscellularcastresult': 'cellular_cast',
    'urinalysiscellularcast': 'cellular_cast',
    'urinalysiscellularca': 'cellular_cast', // API truncated name (ตัดท้าย)
    'cellularcast': 'cellular_cast',
    'cellularcastresult': 'cellular_cast',
    'urinalysiswaxycastresult': 'waxy_cast',
    'urinalysiswaxycast': 'waxy_cast',
    'urinalysiswaxycastno': 'waxy_cast', // API truncated name (ตัดท้าย)
    'waxycast': 'waxy_cast',
    'waxycastresult': 'waxy_cast',

    // Urine Crystal Types
    'urinalysiscalciumoxalateresult': 'calcium_oxalate',
    'urinalysiscalciumoxalate': 'calcium_oxalate',
    'urinalysiscalciumoxa': 'calcium_oxalate', // API truncated name (ตัดท้าย)
    'calciumoxalate': 'calcium_oxalate',
    'calciumoxalateresult': 'calcium_oxalate',
    'urinalysiscalciumcarbonateresult': 'calcium_carbonate',
    'urinalysiscalciumcarbonate': 'calcium_carbonate',
    'urinalysiscalciumcar': 'calcium_carbonate', // API truncated name (ตัดท้าย)
    'calciumcarbonate': 'calcium_carbonate',
    'calciumcarbonateresult': 'calcium_carbonate',
    'urinalysisuricacidcrystalresult': 'uric_acid_crystal',
    'urinalysisuricacidcrystal': 'uric_acid_crystal',
    'urinalysisuricacidcr': 'uric_acid_crystal', // API truncated name (ตัดท้าย)
    'uricacidcrystal': 'uric_acid_crystal',
    'uricacidcrystalresult': 'uric_acid_crystal',
    'urinalysistriplephosphateresult': 'triple_phosphate',
    'urinalysistriplephosphate': 'triple_phosphate',
    'urinalysistriplephos': 'triple_phosphate', // API truncated name (ตัดท้าย)
    'triplephosphate': 'triple_phosphate',
    'triplephosphateresult': 'triple_phosphate',

    // Stool Test
    'stoolcolor': 'stool_color',
    'stoolcolorresult': 'stool_color',       // API: stool_color_result → stripped → stoolcolorresult
    'stoolconsistency': 'stool_consistency',
    'stoolconsistencyresult': 'stool_consistency', // API: stool_consistency_result
    'consistency': 'stool_consistency',
    'consistencyresult': 'stool_consistency',
    'stoolrbc': 'stool_rbc',
    'stoolrbcresult': 'stool_rbc',           // API: stool_rbc_result
    'stoolwbc': 'stool_wbc',
    'stoolwbcresult': 'stool_wbc',           // API: stool_wbc_result
    'stoolparasite': 'stool_parasite',
    'stoolparasiteresult': 'stool_parasite', // API: stool_parasite_result
    'parasite': 'stool_parasite',
    'parasiteresult': 'stool_parasite',
    'stooloccultblood': 'stool_occult_blood',
    'stooloccultbloodresult': 'stool_occult_blood',

    // Glucose result variant (PHP uses $getVal(['glucoseresult', 'fbs']))
    'glucoseresult': 'fbs/fpg',
    'glucose': 'fbs/fpg',

    // Hepatitis (compound API field names)
    'hbsaghbsag': 'hbsag',
    'hbsaghbsagresult': 'hbsag',
    'hbsagresult': 'hbsag',
    'hbsagconcentration': 'hbsag_concentration',
    'hbsagconcentrationresult': 'hbsag_concentration',
    'antihbsantihbs': 'hbsab',
    'antihbsantihbsresult': 'hbsab',
    'antihbs': 'hbsab',                             // 'Anti-HBs' qualitative → HBsAb
    'antihbsresult': 'hbsab',
    'hbsab': 'hbsab',
    'hbsabresult': 'hbsab',
    'antihbsantihbsconcentration': 'anti_hbs',
    'antihbsantihbsconcentrationresult': 'anti_hbs',
    'antihbsantihbsconcen': 'anti_hbs',             // API truncated 'Anti-HBs Anti-HBs Concen...'
    'antihbsantihbsconcenresult': 'anti_hbs',
    'antihbsconcentration': 'anti_hbs',             // 'Anti-HBs Concentration' numeric
    'antihbsconcentrationresult': 'anti_hbs',
    'hcvabhcvab': 'hcv_ab',
    'hcvabhcvabresult': 'hcv_ab',
    'hcvab': 'hcv_ab',
    'hcvabresult': 'hcv_ab',
    'hcvabcoi': 'coi',
    'hcvabcoiresult': 'coi',
    'hcvabcoivalue': 'coi',

    // Tumor Markers (compound API field names)
    'tumormarkerca125': 'ca125',
    'tumormarkerca125result': 'ca125',
    'tumormarkerafp': 'afp',
    'tumormarkerafpresult': 'afp',
    'tumormarkercea': 'cea',
    'tumormarkercearesult': 'cea',
    'tumormarkerca199': 'ca19_9',
    'tumormarkerca199result': 'ca19_9',
    'tumormarkerpsa': 'psa',
    'tumormarkerpsa result': 'psa',
    'tumormarkerpsaresult': 'psa',

    // Thyroid (compound API field names)
    'freet4': 'ft4',
    'freet4result': 'ft4',
    'freet3': 'ft3',
    'freet3result': 'ft3',
    'tshresult': 'tsh',
    'thyroidtsh': 'tsh',
    'thyroidtshresult': 'tsh',
    'thyroidfreet4': 'ft4',
    'thyroidfreet4result': 'ft4',
    'thyroidfreet3': 'ft3',
    'thyroidfreet3result': 'ft3',

    // Electrolytes (compound API field names)
    'electrolytepotassium': 'potassium',
    'electrolytepotassiumresult': 'potassium',
    'electrolytesodium': 'sodium',
    'electrolytesodiumresult': 'sodium',
    'electrolytechloride': 'chloride',
    'electrolytechlorideresult': 'chloride',
    'electrolytecalcium': 'calcium',
    'electrolytecalciumresult': 'calcium',
    'electrolytemagnesium': 'magnesium',
    'electrolytemagnesiumresult': 'magnesium',
    'electrolyteco2': 'bicarbonate',
    'electrolyteco2result': 'bicarbonate',
    'electrolytebicarbonate': 'bicarbonate',
    'electrolytebicarbonateresult': 'bicarbonate',
    'electrolytephosphate': 'phosphate',
    'electrolytephosphateresult': 'phosphate',

    // Blood Glucose - Non-NPO
    'nonnpobloodglucose': 'non_npo_glucose',
    'nonnpo': 'non_npo_glucose',
    'diabetesnonnpo': 'non_npo_glucose',
    'nonnpoglucose': 'non_npo_glucose',
    'randombloodsugar': 'non_npo_glucose',
    'rbs': 'non_npo_glucose',

    // CBC - RBC morphology
    'cbcrbcmorphology': 'rbc_morphology',
    'rbcmorphology': 'rbc_morphology',
    'rbcmorph': 'rbc_morphology',
    'cbcrbcmorph': 'rbc_morphology',
    'rbcmorphologyresult': 'rbc_morphology',

    // Pesticide (compound API field names)
    'totalcholinesterase': 'cholinesterase',
    'totalcholinesteraseresult': 'cholinesterase',
    'pesticidecholinesterase': 'cholinesterase',
    'pesticidecholinesteraseresult': 'cholinesterase'
  };

  const normalizedName = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  return nameMap[normalizedName] || normalizedName;
};


/**
 * Helper: ดึงค่าล่าสุดของ parameter
 */
export const getLatestValue = (labData, paramName) => {
  if (!labData || !labData[paramName] || labData[paramName].length === 0) {
    return null;
  }
  return labData[paramName][0]; // ค่าแรกคือค่าล่าสุด (เรียงลำดับแล้ว)
};

/**
 * Helper: ดึงประวัติทั้งหมดของ parameter
 */
export const getHistory = (labData, paramName) => {
  if (!labData || !labData[paramName]) {
    return [];
  }
  return labData[paramName];
};

/**
 * Helper: ดึงค่า Blood Pressure (รวม systolic/diastolic)
 */
export const getBloodPressure = (labData) => {
  const systolic = getLatestValue(labData, 'systolic');
  const diastolic = getLatestValue(labData, 'diastolic');

  if (systolic && diastolic) {
    return {
      value: `${systolic.value}/${diastolic.value}`,
      date: systolic.date
    };
  }
  return null;
};

/**
 * Helper: ดึงข้อมูล Urine Test ทั้งหมด
 */
export const getUrineTestData = (labData) => {
  const urineParams = [
    'urinalysis_ph', 'urinalysis_color', 'urinalysis_appearance',
    'urinalysis_specific_gravity', 'urinalysis_protein', 'urinalysis_glucose',
    'urinalysis_ketone', 'urinalysis_blood', 'urinalysis_bilirubin',
    'urinalysis_urobilinogen', 'urinalysis_leukocyte', 'urinalysis_nitrite',
    'urinalysis_rbc', 'urinalysis_wbc'
  ];

  const urineData = {};
  let hasData = false;

  urineParams.forEach(param => {
    const value = getLatestValue(labData, param);
    if (value) {
      urineData[param] = value.value;
      hasData = true;
    }
  });

  return hasData ? urineData : null;
};

/**
 * Helper: ดึงข้อมูล Stool Test ทั้งหมด
 */
export const getStoolTestData = (labData) => {
  const stoolParams = [
    'stool_color', 'stool_consistency', 'stool_rbc',
    'stool_wbc', 'stool_parasite', 'stool_occult_blood'
  ];

  const stoolData = {};
  let hasData = false;

  stoolParams.forEach(param => {
    const value = getLatestValue(labData, param);
    if (value) {
      stoolData[param] = value.value;
      hasData = true;
    }
  });

  return hasData ? stoolData : null;
};

export default {
  getMemberLabData,
  getLatestValue,
  getHistory,
  getBloodPressure,
  getUrineTestData,
  getStoolTestData
};
